/**
 * The forecast's projection freshness (#2192). The projected layers come from
 * the projection store, which the server recomputes in the background after
 * every change of a contract, tariff or anything else they depend on.
 *
 * - `pending`: an input changed since `projection.computed_at`. The answer
 *   carries the previous numbers; the screen shows them with a quiet
 *   "Updating" note (no blur, no spinner over them) and asks again every 30 s.
 * - `missing`: the org's first run has not finished (only right after the
 *   deploy that added the store). The fast leg then has no year-end; the
 *   screen keeps its skeleton, and after 15 minutes offers a retry.
 *
 * A server before #2192 sends only `projection_pending`, which meant
 * "missing". Same contract as the phone's
 * mobile-shared/src/api/hooks/useProjectionPoll.ts.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export const PROJECTION_POLL_INTERVAL_MS = 30_000;
/** Longer than the server's PROJECTION_MAX_WAIT (10 min) plus a recompute. */
export const PROJECTION_POLL_MAX_MS = 15 * 60_000;

export interface PollableQuery {
  data?: unknown;
  refetch: (options?: { cancelRefetch?: boolean }) => Promise<unknown>;
}

interface ProjectionFields {
  projection?: { computed_at?: unknown; pending?: unknown };
  projection_pending?: unknown;
}

function fields(data: unknown): ProjectionFields | null {
  return typeof data === "object" && data !== null ? (data as ProjectionFields) : null;
}

/** True when an input changed since the answer's numbers were computed. */
export function isProjectionPending(data: unknown): boolean {
  const f = fields(data);
  if (!f) return false;
  if (f.projection && typeof f.projection.pending === "boolean") return f.projection.pending;
  return f.projection_pending === true;
}

/** True when the answer has no projection yet (the org's first run). */
export function isProjectionMissing(data: unknown): boolean {
  const f = fields(data);
  if (!f || !isProjectionPending(data)) return false;
  if (f.projection) return typeof f.projection.computed_at !== "string";
  return true;
}

/** When the answer's projected numbers were computed, or null. */
export function projectionComputedAt(data: unknown): Date | null {
  const at = fields(data)?.projection?.computed_at;
  if (typeof at !== "string") return null;
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface ProjectionPoll {
  /** Some answer's numbers are about to be updated. */
  pending: boolean;
  /** Some answer has no projection yet: the caller keeps its skeleton. */
  missing: boolean;
  /** Missing for 15 minutes of polling: the caller shows its retry. */
  expired: boolean;
  /** Polls again from the start (the retry). */
  restart: () => void;
}

export function useProjectionPoll(queries: readonly PollableQuery[]): ProjectionPoll {
  const pending = queries.some((q) => isProjectionPending(q.data));
  const missing = queries.some((q) => isProjectionMissing(q.data));
  const latest = useRef(queries);
  useEffect(() => {
    latest.current = queries;
  });
  const [run, setRun] = useState(0);
  const [expiredRun, setExpiredRun] = useState<number | null>(null);

  useEffect(() => {
    if (!pending) return undefined;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= PROJECTION_POLL_MAX_MS) {
        clearInterval(timer);
        setExpiredRun(run);
        return;
      }
      for (const q of latest.current) {
        if (isProjectionPending(q.data)) void q.refetch({ cancelRefetch: false });
      }
    }, PROJECTION_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pending, run]);

  const restart = useCallback(() => {
    setRun((r) => r + 1);
    for (const q of latest.current) void q.refetch({ cancelRefetch: false });
  }, []);

  return { pending, missing, expired: missing && expiredRun === run, restart };
}
